import { lazy } from 'react';
import type { AppContextConfig, AppContextType } from './AppContext.types';
import {
  SvgWorkplaceOctagon,
  SvgDevKit,
  SvgSettings,
  SvgHome,
} from '@opal/icons';

// ─── Registry ────────────────────────────────────────────────────────────────

/**
 * Central registry of all application contexts.
 * Order matters — defines display order in ContextSwitcherRail.
 *
 * SidebarContent is lazy-loaded via React.lazy() to prevent loading all
 * sidebar code upfront. Each sidebar is fetched only when its context
 * is first activated.
 */
export const CONTEXT_REGISTRY: Record<AppContextType, AppContextConfig> = {
  home: {
    id: 'home',
    label: 'Home',
    icon: SvgHome,
    urlPrefix: '',
    defaultRoute: '/',
    tooltip: 'Personal Home',
    SidebarContent: lazy(() =>
      import('@/shared/ui/sections/sidebar/HomeSidebar').then(m => ({ default: m.HomeSidebar }))
    ),
  },
  workplace: {
    id: 'workplace',
    label: 'Workplace',
    icon: SvgWorkplaceOctagon,
    urlPrefix: '/workplace',
    defaultRoute: '/workplace',
    tooltip: 'Team Workplace',
    SidebarContent: lazy(() =>
      import('@/shared/ui/sections/sidebar/WorkplaceSidebar').then(m => ({ default: m.WorkplaceSidebar }))
    ),
  },
  code: {
    id: 'code',
    label: 'Code',
    icon: SvgDevKit,
    urlPrefix: '/code',
    defaultRoute: '/code',
    tooltip: 'Developer Tools',
    SidebarContent: lazy(() =>
      import('@/shared/ui/sections/sidebar/CodeSidebar').then(m => ({ default: m.CodeSidebar }))
    ),
  },
  admin: {
    id: 'admin',
    label: 'Admin',
    icon: SvgSettings,
    urlPrefix: '/admin',
    defaultRoute: '/admin',
    requiredRole: 'admin',
    tooltip: 'Admin Panel',
    SidebarContent: lazy(() =>
      import('@/shared/ui/sections/sidebar/AdminSidebar').then(m => ({ default: m.default }))
    ),
  },
};

// ─── Helper Functions ─────────────────────────────────────────────────────────

/**
 * Resolve the active context from a URL pathname.
 *
 * Priority: admin > workplace > code > home (default for all other paths).
 *
 * @example
 * resolveContextFromPath('/workplace/campaigns/123') → 'workplace'
 * resolveContextFromPath('/admin/users') → 'admin'
 * resolveContextFromPath('/catalog') → 'home'
 * resolveContextFromPath('/') → 'home'
 */
export function resolveContextFromPath(pathname: string): AppContextType {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/workplace')) return 'workplace';
  if (pathname.startsWith('/code')) return 'code';
  return 'home';
}

/**
 * Get the config object for a given context ID.
 *
 * @throws never — all AppContextType values are in registry
 */
export function getContextConfig(ctx: AppContextType): AppContextConfig {
  return CONTEXT_REGISTRY[ctx];
}

/**
 * Filter and return contexts visible to the current user based on role.
 * Returns configs in display order (home → workplace → code → admin?).
 *
 * @param isAdmin - whether the current user has admin role
 * @param isCurator - whether the current user has curator role
 */
export function getVisibleContexts(
  isAdmin: boolean,
  isCurator: boolean
): AppContextConfig[] {
  return Object.values(CONTEXT_REGISTRY).filter(ctx => {
    if (!ctx.requiredRole) return true;
    if (ctx.requiredRole === 'admin') return isAdmin || isCurator;
    return false;
  });
}
